import { createUnifiedTheme, palettes } from '@backstage/theme';

// Badal.io brand colors extracted from their website
const badalPrimaryBlue = '#1e87f0';
const badalSecondaryGreen = '#32d296';
const badalDarkGrey = '#222';
const badalMediumGrey = '#666';
const badalLightGrey = '#f8f8f8';
const badalBorderGrey = '#e5e5e5';
const badalTextGrey = '#999';

export const badalLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: badalPrimaryBlue,
      light: '#0f7ae5',
      dark: '#0e6ecd',
    },
    secondary: {
      main: badalSecondaryGreen,
      light: '#4AE2A6',
      dark: '#28A876',
    },
    background: {
      default: '#FFFFFF',
      paper: badalLightGrey,
    },
    text: {
      primary: '#333',
      secondary: badalMediumGrey,
    },
    navigation: {
      background: badalPrimaryBlue,
      indicator: badalSecondaryGreen,
      color: '#FFFFFF',
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.1)',
      },
    },
    status: {
      ok: badalSecondaryGreen,
      warning: '#faa05a',
      error: '#f0506e',
      running: badalPrimaryBlue,
      pending: badalTextGrey,
      aborted: '#757575',
    },
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    documentation: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    tool: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    service: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    website: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    library: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    other: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    app: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    apis: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    card: {
      fontColor: '#333',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '4px',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: badalPrimaryBlue,
          '&:hover': {
            backgroundColor: '#0f7ae5',
            boxShadow: '0 4px 8px rgba(30, 135, 240, 0.3)',
          },
        },
        containedSecondary: {
          backgroundColor: badalSecondaryGreen,
          '&:hover': {
            backgroundColor: '#28A876',
            boxShadow: '0 4px 8px rgba(50, 210, 150, 0.3)',
          },
        },
        outlined: {
          borderColor: badalBorderGrey,
          '&:hover': {
            borderColor: badalPrimaryBlue,
            backgroundColor: 'rgba(30, 135, 240, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          border: `1px solid ${badalBorderGrey}`,
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: '48px',
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${badalBorderGrey}`,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: 700,
          color: '#333',
        },
        h2: {
          fontWeight: 600,
          color: '#333',
        },
        h3: {
          fontWeight: 600,
          color: '#333',
        },
        h4: {
          fontWeight: 500,
          color: '#333',
        },
        h5: {
          fontWeight: 500,
          color: '#333',
        },
        h6: {
          fontWeight: 500,
          color: '#333',
        },
      },
    },
  },
});

export const badalDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: badalPrimaryBlue,
      light: '#4DA3F5',
      dark: '#0f6ecd',
    },
    secondary: {
      main: badalSecondaryGreen,
      light: '#5AEDB0',
      dark: '#28A876',
    },
    background: {
      default: '#1A1A1A',
      paper: badalDarkGrey,
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B3B3B3',
    },
    navigation: {
      background: badalDarkGrey,
      indicator: badalSecondaryGreen,
      color: '#FFFFFF',
      selectedColor: badalSecondaryGreen,
      navItem: {
        hoverBackground: 'rgba(30, 135, 240, 0.1)',
      },
    },
    status: {
      ok: badalSecondaryGreen,
      warning: '#faa05a',
      error: '#f0506e',
      running: badalPrimaryBlue,
      pending: '#B0B0B0',
      aborted: '#808080',
    },
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    documentation: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    tool: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    service: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    website: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    library: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    other: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    app: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    apis: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
    card: {
      fontColor: '#FFFFFF',
      shape: 'none',
      colors: [],
      backgroundImage: '',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#1A1A1A',
        },
        html: {
          backgroundColor: '#1A1A1A',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '4px',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: badalDarkGrey,
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: badalDarkGrey,
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: '48px',
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #3C3C3C',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: 700,
        },
        h2: {
          fontWeight: 600,
        },
        h3: {
          fontWeight: 600,
        },
        h4: {
          fontWeight: 500,
        },
        h5: {
          fontWeight: 500,
        },
        h6: {
          fontWeight: 500,
        },
      },
    },
  },
});